import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {
    AddButton,
    AddCircleBlue,
    CustomDelete, EditWithIcon,
    ManageActions,
    MinLoader,
    MoreActions,
    NoItemsYet,
    SimpleTooltip,
    TextRow,
} from 'components';
import {TargetsChooseFromLibrary} from 'fragments';
import {LibraryTargetDetails} from './libraryTargetDetails';
import {Colors, FindLoad, FindSuccessItem, Images, useBaseModal, useModal, PermissionList, RolePermission} from 'utils';
import {libraryActions, httpRequestsOnSuccessActions} from 'store';
import {Svg} from 'assets';

export const LibraryGoalTarget = () => {
    const {goalById} = useSelector((state) => ({
        goalById: state.library.goalById,
    }));

    const dispatch = useDispatch();
    const [targetRow, setTargetRow] = useState(null);
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const [targets, setTargets] = useState([]);
    const TARGET_ACTION_TYPE = inputs?.id ? 'EDIT_LIBRARY_GOAL_TARGET' : 'CREATE_LIBRARY_GOAL_TARGET';
    const loadAdd = FindLoad(TARGET_ACTION_TYPE);
    const successCreate = FindSuccessItem(TARGET_ACTION_TYPE);
    const {open} = useModal();
    const {openBase} = useBaseModal();
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);

    useEffect(() => {
        if (goalById?.targets?.length) {
            setTargets(goalById.targets);
        } else {
            setTargets([]);
        }
    }, [goalById]);

    useEffect(() => {
        if (successCreate) {
            resetCreation();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(TARGET_ACTION_TYPE));
        }
    }, [successCreate]);

    const resetCreation = () => {
        setTargetRow(null);
        setError('');
        setInputs({});
    };

    const handleSave = () => {
        if (inputs?.name) {
            const params = {
                ...inputs,
                goal: goalById?.id,
            };
            if (inputs?.id) {
                delete params?.id;
                delete params?.updatedAt;
                delete params?.createdAt;
                dispatch(libraryActions.editLibraryGoalTarget(goalById?.id, inputs?.id, params));
            } else {
                dispatch(libraryActions.createLibraryGoalTarget(goalById?.id, params));
            }
        } else {
            setError('name');
        }
    };

    const handleChange = (e) => {
        setInputs((prevState) => ({...prevState, [e.target.name]: e.target.value}));
        if (error === e.target.name) {
            setError('');
        }
    };

    const handleDragEnd = (result) => {
        const {source, destination} = result;
        if (!destination || source.index === destination.index) return;
        const updatedTargets = Array.from(targets);
        const [movedItem] = updatedTargets.splice(source.index, 1);
        updatedTargets.splice(destination.index, 0, movedItem);
        setTargets(updatedTargets);

        const updatedGoal = {
            ...goalById,
            targets: updatedTargets,
        };

        // dispatch(libraryActions.reorderLibraryGoalTarget(updatedGoal, {
        //     indexFrom: source.index,
        //     indexTo: destination.index,
        // }));

        dispatch(
            libraryActions.reorderLibraryGoalTarget(goalById?.id, {
                indexFrom: source.index,
                indexTo: destination.index,
            })
        );
    };

    const handleAction = (ev, item, name) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (name === 'edit') {
            setTargetRow(item?.id);
            setInputs(item);
        }

        if (name === 'delete') {
            open(
                <CustomDelete
                    text="Are you sure you want to delete this target? This action cannot be undone."
                    info="Delete Target"
                    handleDel={() => dispatch(libraryActions.deleteLibraryGoalTarget(goalById?.id, item?.id))}
                    actionType={'DELETE_LIBRARY_GOAL_TARGET'}
                    innerText={'Delete'}
                />
            );
        }
    };

    const addTarget = (closePopover) => {
        closePopover?.();

        setTargetRow('new');
        setInputs({});
    };

    const openTargetDetails = (target) => {
        openBase(<LibraryTargetDetails target={target}/>);
    };

    return (
        <div>
            {targets?.length > 0 ? (
                <div className="goal-target-wrapper">
                    <div className="goal-target-header">
                        <div style={{width: '5%'}}>#</div>
                        <div style={{width: '26%'}}>Target Name</div>
                        <div style={{width: '59%'}}>Objective</div>
                        <div style={{width: '10%'}}>Action</div>
                    </div>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="targetList">
                            {(provided) => (
                                <div className="goal-target-body-wrapper" {...provided.droppableProps}
                                     ref={provided.innerRef}>
                                    {targets.map((i, index) => (
                                        <Draggable key={i.id} draggableId={i.id.toString()} index={index}
                                                   isDragDisabled={!hasPermission}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                    }}>
                                                    {targetRow === i?.id ? (
                                                        <div className="create-target-wrapper">
                                                            <input
                                                                value={inputs?.name || ''}
                                                                onChange={handleChange}
                                                                placeholder={'Task Name'}
                                                                name="name"
                                                                type="text"
                                                                style={{
                                                                    width: '25%',
                                                                    borderColor: error === 'name' ? '#FE7070' : '#E4E7EC',
                                                                }}
                                                            />
                                                            <input
                                                                value={inputs?.description || ''}
                                                                onChange={handleChange}
                                                                placeholder={'Add Objective '}
                                                                name="description"
                                                                type="text"
                                                                style={{
                                                                    width: '65%',
                                                                    borderColor: error === 'description' ? '#FE7070' : '#E4E7EC',
                                                                }}
                                                            />
                                                            <div className="flex-align-center"
                                                                 style={{gap: 4, width: '10%'}}>
                                                                <button onClick={handleSave} className="add-target-btn">
                                                                    {loadAdd?.length ? (
                                                                        <MinLoader
                                                                            margin="3px 0 0"
                                                                            color={Colors.TextWhite}
                                                                            position="relative"
                                                                        />
                                                                    ) : (
                                                                        'Save'
                                                                    )}
                                                                </button>
                                                                <button onClick={resetCreation}
                                                                        className="cancel-add-target-btn">
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => openTargetDetails(i)}
                                                            className="goal-target-body"
                                                            style={{
                                                                border: `1px solid ${snapshot.isDragging ? '#0C66E3' : '#F9FAFB'}`,
                                                                background: '#F9FAFB',
                                                            }}>
                                                            <div className="goal-target-body-row" style={{width: '5%'}}>
                                                                {index + 1}
                                                            </div>
                                                            <div
                                                                className="goal-target-body-row flex-align-center"
                                                                style={{width: '26%', gap: '4px'}}>
                                                                {!i?.mastery?.followGoalSettings && (
                                                                    <SimpleTooltip
                                                                        title={
                                                                            <p>{'This target has its own mastery.'}</p>}
                                                                        placement="bottom"
                                                                        arrow>
                                                                        <img src={Images.masteredTarget}
                                                                             alt="mastered"/>
                                                                    </SimpleTooltip>
                                                                )}
                                                                <TextRow name={i?.name}/>
                                                            </div>
                                                            <div className="goal-target-body-row" style={{width: '59%'}}>
                                                                <p style={{height: 36, overflow: 'auto'}}>{i?.description} </p>
                                                                {/*<TextRow name={i?.description}/>*/}
                                                            </div>

                                                            <div className="goal-target-body-row" style={{width: '10%', gap: 16}}>
                                                                {hasPermission &&
                                                                    <>
                                                                        <EditWithIcon
                                                                            handleClick={(e) => handleAction(e, i, 'edit')}
                                                                            icon={Images.edit}
                                                                        />
                                                                        <EditWithIcon
                                                                            handleClick={(e) => handleAction(e, i, 'delete')}
                                                                            icon={Images.deleteTag}/>
                                                                    </>
                                                                }


                                                                {/*{hasPermission && (*/}
                                                                {/*    <MoreActions*/}
                                                                {/*        item={i}*/}
                                                                {/*        buttonsList={[*/}
                                                                {/*            {*/}
                                                                {/*                text: 'Edit',*/}
                                                                {/*                icon: Images.edit,*/}
                                                                {/*                actionName: 'edit',*/}
                                                                {/*            },*/}
                                                                {/*            {*/}
                                                                {/*                text: 'Delete',*/}
                                                                {/*                icon: Images.deleteTag,*/}
                                                                {/*                actionName: 'delete',*/}
                                                                {/*            },*/}
                                                                {/*        ]}*/}
                                                                {/*        handleAction={handleAction}*/}
                                                                {/*    />*/}
                                                                {/*)}*/}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            ) : (
                <NoItemsYet
                    height={'250px'}
                    text={'You don’t have Targets yet'}
                    subTitle={'Nothing new for now. Enjoy the silence while it lasts'}
                    image={Images.noClientYet}
                    containerStyles={{position: 'relative'}}
                    renderButton={() =>
                        hasPermission ? (
                            <ManageActions
                                btnClass="reset-btn"
                                renderBtnView={() => (
                                    <div style={{width: 'fit-content', marginTop: 24}}>
                                        <AddButton styles={{width: 'fit-content'}} text={'Add Target'}/>
                                    </div>
                                )}
                                renderBody={(closePopover) => (
                                    <div className="flex" style={{flexDirection: 'column'}}>
                                        <button className="header-add-goals-items-btn"
                                                onClick={() => addTarget(closePopover)}>
                                            Add new target
                                        </button>

                                        <button
                                            className="header-add-goals-items-btn"
                                            onClick={() => {
                                                open(
                                                    <TargetsChooseFromLibrary
                                                        CHOOSE_ACTION_TYPE={'CHOOSE_TARGETS_FROM_LIBRARY_GOAL'}
                                                        action={libraryActions.chooseTargetsFromLibraryGoal}
                                                        fromTheLibrary
                                                        goalById={goalById}
                                                    />,
                                                    {
                                                        styles: {
                                                            wrapperStyle: {
                                                                width: 'calc(100vw - 32px)',
                                                                maxWidth: '1370px',
                                                            },
                                                        },
                                                    }
                                                );
                                                closePopover?.();
                                            }}>
                                            Choose from Library
                                        </button>
                                    </div>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                styleBodyItems={{padding: 0}}
                            />
                        ) : null
                    }
                />
            )}

            {targetRow === 'new' && (
                <div className="create-target-wrapper">
                    <input
                        value={inputs?.name || ''}
                        onChange={handleChange}
                        placeholder={'Task Name'}
                        name="name"
                        type="text"
                        style={{width: '25%', borderColor: error === 'name' ? '#FE7070' : '#E4E7EC'}}
                    />
                    <input
                        value={inputs?.description || ''}
                        onChange={handleChange}
                        placeholder={'Add Objective '}
                        name="description"
                        type="text"
                        style={{width: '65%', borderColor: error === 'description' ? '#FE7070' : '#E4E7EC'}}
                    />
                    <div className="flex-align-center" style={{gap: 4, width: '10%'}}>
                        <button onClick={handleSave} className="add-target-btn">
                            {loadAdd?.length ?
                                <MinLoader margin="3px 0 0" color={Colors.TextWhite} position="relative"/> : 'Add'}
                        </button>
                        <button onClick={resetCreation} className="cancel-add-target-btn">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {!targetRow && !!targets?.length && hasPermission && (
                <div className="flex-end" style={{margin: '8px 0 0'}}>
                    <ManageActions
                        btnClass="reset-btn"
                        renderBtnView={(isOpen) => (
                            <div className="flex-align-center" style={{gap: 8}}>
                                <AddCircleBlue text={'Add Target'}/>

                                <Svg.ArrowRight2Svg
                                    style={{
                                        transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                        filter: 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(1578%) hue-rotate(201deg) brightness(90%) contrast(100%)',
                                    }}
                                />
                            </div>
                        )}
                        renderBody={(closePopover) => (
                            <div className="flex" style={{flexDirection: 'column'}}>
                                <button className="header-add-goals-items-btn" onClick={() => addTarget(closePopover)}>
                                    Add new target
                                </button>

                                <button
                                    className="header-add-goals-items-btn"
                                    onClick={() => {
                                        open(
                                            <TargetsChooseFromLibrary
                                                CHOOSE_ACTION_TYPE={'CHOOSE_TARGETS_FROM_LIBRARY_GOAL'}
                                                action={libraryActions.chooseTargetsFromLibraryGoal}
                                                fromTheLibrary
                                                goalById={goalById}
                                            />,
                                            {
                                                styles: {
                                                    wrapperStyle: {
                                                        width: 'calc(100vw - 32px)',
                                                        maxWidth: '1370px',
                                                    },
                                                },
                                            }
                                        );
                                        closePopover?.();
                                    }}>
                                    Choose from Library
                                </button>
                            </div>
                        )}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        styleBodyItems={{padding: 0}}
                    />
                </div>
            )}
        </div>
    );
};
